import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart } from './_finHelpers';

// APR Calculator — General + Mortgage (matches image)
export default function APRCalc() {
  return (
    <CalcShell look="cnet" title="APR Calculator" subtitle="Compute the true annual percentage rate accounting for fees, points, and PMI."
      about={<>
        <p>The <b>APR (Annual Percentage Rate)</b> represents the true annual cost of a loan including interest plus fees and points. It is usually higher than the stated interest rate.</p>
      </>}
    >
      <GeneralAPR/>
      <hr className="my-8 border-slate-200" />
      <MortgageAPR/>
    </CalcShell>
  );
}

function GeneralAPR() {
  const [amount, setAmount] = useState(100000);
  const [years, setYears] = useState(10);
  const [months, setMonths] = useState(0);
  const [rate, setRate] = useState(6);
  const [compound, setCompound] = useState(12);
  const [payback, setPayback] = useState(12);
  const [loanedFees, setLoanedFees] = useState(0);
  const [upfrontFees, setUpfrontFees] = useState(2500);
  const c = useMemo(() => {
    const n = years*12 + months;
    const loanBase = amount + loanedFees;
    const r = rate/100/12;
    const M = r === 0 ? loanBase/n : (loanBase*r*Math.pow(1+r,n))/(Math.pow(1+r,n)-1);
    const totalPmt = M * n;
    const interest = totalPmt - loanBase;
    // APR: solve for rate that equates (amount - upfrontFees) to payment stream
    const cash = amount - upfrontFees;
    let lo = 0, hi = 1, apr = rate;
    for (let it = 0; it < 60; it++) {
      const mid = (lo + hi) / 2;
      const pv = M * (1 - Math.pow(1+mid, -n)) / (mid || 1e-9);
      if (pv > cash) lo = mid; else hi = mid;
    }
    apr = ((lo + hi) / 2) * 12 * 100;
    return { M, totalPmt, interest, apr, allTotal: totalPmt + upfrontFees };
  }, [amount, years, months, rate, compound, payback, loanedFees, upfrontFees]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-3">General APR Calculator</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Loan Amount</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={amount} onChange={e=>setAmount(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Loan Term</label>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex gap-2"><input type="number" value={years} onChange={e=>setYears(+e.target.value||0)}/><span className="self-center text-slate-500">years</span></div>
              <div className="flex gap-2"><input type="number" value={months} onChange={e=>setMonths(+e.target.value||0)}/><span className="self-center text-slate-500">months</span></div>
            </div>
          </div>
          <div className="field"><label>Interest Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Compound</label>
            <select value={compound} onChange={e=>setCompound(+e.target.value)}><option value={12}>Monthly</option><option value={365}>Daily</option><option value={4}>Quarterly</option><option value={1}>Annually</option></select>
          </div>
          <div className="field"><label>Pay Back</label>
            <select value={payback} onChange={e=>setPayback(+e.target.value)}><option value={12}>Every Month</option><option value={4}>Every Quarter</option><option value={1}>Every Year</option></select>
          </div>
          <div className="field"><label>Loaned Fees</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={loanedFees} onChange={e=>setLoanedFees(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Upfront Fees</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={upfrontFees} onChange={e=>setUpfrontFees(+e.target.value||0)} className="flex-1"/></div></div>
        </div>
        <div>
          <div className="result-panel mb-4"><div className="result-title">Real APR</div><div className="result-value" data-testid="apr-general">{c.apr.toFixed(3)}%</div></div>
          <table className="data-table"><tbody>
            <tr><td>Amount Financed</td><td className="text-right">{fmt(amount)}</td></tr>
            <tr><td>Upfront Out-of-Pocket Fees</td><td className="text-right">{fmt(upfrontFees)}</td></tr>
            <tr><td>Payment Every Month</td><td className="text-right font-semibold">{fmt(c.M)}</td></tr>
            <tr><td>Total of {years*12+months} Payments</td><td className="text-right">{fmt(c.totalPmt)}</td></tr>
            <tr><td>Total Interest</td><td className="text-right text-orange-600">{fmt(c.interest)}</td></tr>
            <tr><td>All Payments and Fees</td><td className="text-right font-semibold">{fmt(c.allTotal)}</td></tr>
          </tbody></table>
          <div className="mt-4"><PieChart parts={[{label:'Principal',value:amount,color:'#2563eb'},{label:'Interest',value:c.interest,color:'#10b981'},{label:'Fees',value:upfrontFees+loanedFees,color:'#dc2626'}]} size={160}/></div>
        </div>
      </div>
    </div>
  );
}

function MortgageAPR() {
  const [houseValue, setHouseValue] = useState(350000);
  const [downPct, setDownPct] = useState(20);
  const [term, setTerm] = useState(30);
  const [rate, setRate] = useState(6.2);
  const [loanFees, setLoanFees] = useState(3500);
  const [points, setPoints] = useState(0.5);
  const [pmi, setPmi] = useState(0);
  const c = useMemo(() => {
    const down = houseValue * downPct/100;
    const loan = houseValue - down;
    const pointsCost = loan * points/100;
    const totalFees = loanFees + pointsCost;
    const r = rate/100/12, n = term*12;
    const M = r === 0 ? loan/n : (loan*r*Math.pow(1+r,n))/(Math.pow(1+r,n)-1);
    const totalPmt = M * n + pmi * term;
    const cash = loan - totalFees;
    let lo = 0, hi = 1;
    for (let it = 0; it < 60; it++) {
      const mid = (lo + hi) / 2;
      const pv = M * (1 - Math.pow(1+mid, -n)) / (mid || 1e-9);
      if (pv > cash) lo = mid; else hi = mid;
    }
    const apr = ((lo + hi) / 2) * 12 * 100;
    return { loan, down, M, totalPmt, apr, totalFees, allTotal: totalPmt + totalFees };
  }, [houseValue, downPct, term, rate, loanFees, points, pmi]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-3">Mortgage APR Calculator</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>House Value</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={houseValue} onChange={e=>setHouseValue(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Down Payment</label><div className="flex gap-2"><input type="number" step="0.01" value={downPct} onChange={e=>setDownPct(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Loan Term</label><div className="flex gap-2"><input type="number" value={term} onChange={e=>setTerm(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">years</span></div></div>
          <div className="field"><label>Interest Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Loan Fees</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={loanFees} onChange={e=>setLoanFees(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Points</label><div className="flex gap-2"><input type="number" step="0.01" value={points} onChange={e=>setPoints(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>PMI Insurance</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={pmi} onChange={e=>setPmi(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">/year</span></div></div>
        </div>
        <div>
          <div className="result-panel mb-4"><div className="result-title">Real APR</div><div className="result-value">{c.apr.toFixed(3)}%</div></div>
          <table className="data-table"><tbody>
            <tr><td>Loan Amount</td><td className="text-right">{fmt(c.loan)}</td></tr>
            <tr><td>Down Payment</td><td className="text-right">{fmt(c.down)}</td></tr>
            <tr><td>Monthly Pay</td><td className="text-right font-semibold">{fmt(c.M)}</td></tr>
            <tr><td>Total of {term*12} Payments</td><td className="text-right">{fmt(c.totalPmt)}</td></tr>
            <tr><td>Total Interest</td><td className="text-right text-orange-600">{fmt(c.totalPmt - c.loan)}</td></tr>
            <tr><td>All Payments and Fees</td><td className="text-right font-semibold">{fmt(c.allTotal)}</td></tr>
          </tbody></table>
          <div className="mt-4"><PieChart parts={[{label:'Principal',value:c.loan,color:'#2563eb'},{label:'Interest',value:c.totalPmt-c.loan,color:'#10b981'},{label:'Fees',value:c.totalFees,color:'#dc2626'}]} size={160}/></div>
        </div>
      </div>
    </div>
  );
}
